import api from "../index";

// Types
export interface Plan {
  _id: string;
  name: string;
  slug?: string;
  description?: string;
  metaDescription?: string;
  tier?: number;
  planType?: string;
  isPopular?: boolean;
  weeklyPrice?: number;
  monthlyPrice?: number;
  annualPrice?: number;
  credits?: number;
  features?: Array<{
    category?: string;
    name?: string;
    description?: string;
    included?: boolean;
    limit?: string;
    highlight?: boolean;
    [key: string]: any;
  } | string>;
  stripeWeeklyPriceId?: string;
  stripeWeeklyMeteredPriceId?: string;
  stripeMonthlyPriceId?: string;
  stripeMonthlyMeteredPriceId?: string;
  stripeAnnualPriceId?: string;
  stripeAnnualMeteredPriceId?: string;
}

export interface SubscriptionStatus {
  plan: string;
  tier: number;
  status: "active" | "canceled" | "past_due" | "trialing" | "incomplete";
  interval: "week" | "month" | "year" | "lifetime" | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  isAppSumo: boolean;
  isLifetime: boolean;
}

export interface UsageMetrics {
  creditsUsed: number;
  creditsAllotted: number;
  creditsLeft: number;
}

export interface ProrationPreview {
  success: boolean;
  immediateCharge: number;
  credit: number;
  netAmount: number;
  nextBillingDate: string;
  nextBillingAmount: number;
  currency: string;
  error?: string;
}

// Plan tier mapping for comparison
export const PLAN_TIERS: Record<string, number> = {
  Free: 0,
  Optimizer: 1,
  Wizard: 2,
  Maverick: 3,
};

/**
 * Get tier from plan name (case-insensitive)
 */
export const getTierFromPlanName = (planName: string | undefined): number => {
  if (!planName) return 0;
  const normalized = Object.keys(PLAN_TIERS).find(
    (key) => key.toLowerCase() === planName.toLowerCase()
  );
  return normalized ? PLAN_TIERS[normalized] : 0;
};

/**
 * Plan action types
 */
export type PlanAction = "current" | "switch-interval" | "upgrade" | "downgrade" | "downgrade-free";

/**
 * Determine button action type based on current vs target plan
 * Always uses getTierFromPlanName for accurate tier comparison (ignores database tier field)
 */
export const getPlanAction = (
  currentPlan: string | undefined,
  currentTier: number,
  currentInterval: string | null | undefined,
  targetPlan: Plan,
  selectedInterval: "week" | "month" | "year"
): PlanAction => {
  // Always use tier mapping for accurate comparison (database tier may be incorrect)
  const actualCurrentTier = getTierFromPlanName(currentPlan);
  const targetTier = getTierFromPlanName(targetPlan.name);
  const isSamePlan = currentPlan?.toLowerCase() === targetPlan.name.toLowerCase();

  // Check if it's the same plan AND same interval
  if (isSamePlan && currentInterval === selectedInterval) {
    return "current";
  }

  // Same plan but different interval - allow switching billing period
  if (isSamePlan && currentInterval !== selectedInterval) {
    return "switch-interval";
  }

  // Check if target is Free plan
  const isTargetFree = targetPlan.name.toLowerCase() === "free" || targetTier === 0;
  if (isTargetFree) {
    return "downgrade-free";
  }

  // Compare tiers using our tier mapping
  if (targetTier > actualCurrentTier) {
    return "upgrade";
  } else {
    return "downgrade";
  }
};

/**
 * Get price for the selected billing interval with fallback logic.
 * If weeklyPrice is not available, calculate it from monthlyPrice.
 * Prices may be stored in cents or dollars depending on plan structure.
 */
export const getPriceForInterval = (plan: Plan, interval: "week" | "month" | "year"): number => {
  if (interval === "week") {
    // Use weeklyPrice if available, otherwise calculate from monthly (monthly / 4)
    return plan.weeklyPrice ?? Math.round((plan.monthlyPrice ?? 0) / 4);
  }
  if (interval === "year") {
    // Show actual annual price (not per-month equivalent)
    return plan.annualPrice ?? 0;
  }
  // Monthly
  return plan.monthlyPrice ?? 0;
};

/**
 * Check if a plan is free (all prices are 0 or undefined)
 */
export const isFreePlan = (plan: Plan): boolean => {
  const monthly = plan.monthlyPrice ?? 0;
  const weekly = plan.weeklyPrice ?? 0;
  const annual = plan.annualPrice ?? 0;
  return monthly === 0 && weekly === 0 && annual === 0;
};

/**
 * Update subscription to a new plan or billing interval
 * Handles proration automatically - user pays the difference or gets credit
 * Uses the billing controller endpoint
 */
export const updateSubscription = async (planId: string, interval: "week" | "month" | "year") => {
  return await api.post("/billing/subscription/update", { planId, interval });
};

/**
 * Cancel subscription at period end (downgrade to Free)
 * The subscription remains active until the current billing period ends
 * Uses the existing subscriptions/cancel endpoint
 */
export const cancelSubscription = async () => {
  return await api.post("/subscriptions/cancel");
};

/**
 * Preview proration for a subscription change
 * Shows what the user will be charged or credited
 * Uses the billing controller endpoint
 */
export const previewSubscriptionChange = async (planId: string, interval: "week" | "month" | "year") => {
  return await api.post("/billing/subscription/preview", { planId, interval });
};

/**
 * Get subscription status
 */
export const getSubscriptionStatus = async () => {
  return await api.get("/admin/subscription");
};

/**
 * Get public plans
 */
export const getPlans = async () => {
  return await api.get("/plans");
};
